import { Plus, X, FolderOpen } from 'lucide-react';
import { Modal } from '../ui/Modal';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import VaultPasskeyList from '../VaultPasskeyList';
import { useVaultStore } from '../../store/vaultStore';
import VaultCapModal from '../VaultCapModal';
import { keepItemsOnDevice } from '../../services/vaultCloudCap';
import { VAULT_TYPE_META } from './vaultItemData';
import { FieldLabel, PasswordInput } from './VaultItemFormFields';

/** The vault item modal markup: type picker step and the form. Plain function called during render, so the element tree is unchanged. */
export function renderVaultItemModal({
  t,
  values,
  item,
  type,
  destinations,
  destination,
  onClose,
  ephemeralOption,
  setValue,
  saving,
  capDraft,
  setCapDraft,
  error,
  keySource,
  setKeySource,
  keyFileName,
  keyFileError,
  keyFileInputRef,
  teamFolderId,
  setTeamFolderId,
  setCreatingFolder,
  pickKeyFile,
  onKeyFileInputChange,
  NEW_FOLDER,
  existingFolders,
  creatingFolder,
  changeFolder,
  EPHEMERAL_DESTINATION,
  offerEphemeral,
  isEphemeral,
  showDestination,
  isTeamDestination,
  teamFolderOptions,
  teamRootWritable,
  changeDestination,
  changeEngine,
  changeProtocol,
  updateCustomField,
  addCustomField,
  removeCustomField,
  canSave,
  handleSubmit,
}) {
  const typeMeta = VAULT_TYPE_META[type] || VAULT_TYPE_META.custom;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={item ? t('vault.editItem') : t('vault.newItem')}
      subtitle={t(typeMeta.labelKey)}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="ext-modal-body space-y-4">
          <div>
            <FieldLabel required>{t('vault.fieldName')}</FieldLabel>
            <input
              type="text"
              className="modal-input"
              style={{ width: '100%' }}
              value={values.name}
              onChange={(e) => setValue('name', e.target.value)}
              autoFocus
            />
          </div>

          {/* Destination. Rendered only when there is somewhere else to save
              — a user in no writable team never sees this control at all. */}
          {showDestination && (
            <div>
              <FieldLabel>{t('vault.fieldDestination')}</FieldLabel>
              <CompactSelectPicker
                className="vault-modal-picker"
                value={destination}
                onChange={changeDestination}
                options={[
                  ...destinations.map((d) =>
                    d.kind === 'personal'
                      ? { value: d.value, label: t('vault.destinationPersonal') }
                      : { value: d.value, label: d.teamName }
                  ),
                  ...(offerEphemeral
                    ? [{ value: EPHEMERAL_DESTINATION, label: ephemeralOption.label }]
                    : []),
                ]}
                aria-label={t('vault.fieldDestination')}
              />
              {isEphemeral && ephemeralOption.note && (
                <p className="vault-destination-note">{ephemeralOption.note}</p>
              )}
              {isTeamDestination && (
                // Plain statement of fact, and deliberately NOT an "end-to-end"
                // or "only you can read this" claim: the team vault is
                // Cloak-encrypted at rest, so the server can read it. See
                // src/tests/endToEndClaims.test.js.
                <p className="vault-destination-note">{t('vault.destinationTeamNote')}</p>
              )}
            </div>
          )}

          {isTeamDestination ? (
            <div>
              <FieldLabel>{t('vault.fieldFolder')}</FieldLabel>
              <CompactSelectPicker
                className="vault-modal-picker"
                value={teamFolderId}
                onChange={setTeamFolderId}
                options={[
                  // The team ROOT is a real place that holds items, not "no
                  // folder" — and it is offered only when root itself is
                  // writable, because a member may hold a folder grant without
                  // any right to the root.
                  ...(teamRootWritable
                    ? [{ value: '', label: t('vault.teamRootFolder') }]
                    : []),
                  ...teamFolderOptions,
                ]}
                aria-label={t('vault.fieldFolder')}
              />
            </div>
          ) : (
          <div>
            <FieldLabel>{t('vault.fieldFolder')}</FieldLabel>
            {creatingFolder ? (
              <div className="ext-field-row">
                <input
                  type="text"
                  className="modal-input"
                  style={{ width: '100%' }}
                  placeholder={t('vault.folderNamePlaceholder')}
                  value={values.folder}
                  onChange={(e) => setValue('folder', e.target.value)}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <button
                  type="button"
                  className="modal-btn-ghost"
                  onClick={() => {
                    // Back out cleanly: an abandoned half-typed name must not
                    // survive as the item's folder.
                    setCreatingFolder(false);
                    setValue('folder', '');
                  }}
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
            ) : (
              <CompactSelectPicker
                className="vault-modal-picker"
                value={values.folder || ''}
                onChange={changeFolder}
                options={[
                  { value: '', label: t('vault.noFolder') },
                  ...existingFolders.map((f) => ({ value: f, label: f })),
                  { value: NEW_FOLDER, label: t('vault.createFolder') },
                ]}
                aria-label={t('vault.fieldFolder')}
              />
            )}
          </div>
          )}

          {type === 'password' && (
            <>
              <div>
                <FieldLabel>{t('vault.fieldUrl')}</FieldLabel>
                <input
                  type="text"
                  className="modal-input"
                  style={{ width: '100%' }}
                  placeholder="https://example.com"
                  value={values.url}
                  onChange={(e) => setValue('url', e.target.value)}
                />
              </div>
              <div>
                <FieldLabel>{t('vault.fieldUsername')}</FieldLabel>
                <input
                  type="text"
                  className="modal-input"
                  style={{ width: '100%' }}
                  value={values.username}
                  onChange={(e) => setValue('username', e.target.value)}
                />
              </div>
              <div>
                <FieldLabel>{t('vault.fieldPassword')}</FieldLabel>
                <PasswordInput
                  value={values.password}
                  onChange={(v) => setValue('password', v)}
                  withGenerator
                  generateLabel={t('vault.generatePassword')}
                />
              </div>
              <div>
                <FieldLabel>{t('vault.fieldTotp')}</FieldLabel>
                <PasswordInput value={values.totp} onChange={(v) => setValue('totp', v)} />
              </div>
              {/* Passkeys are not form fields: rename/delete write straight
                  through their own commands and the list re-reads itself, so
                  nothing here ever reaches `data`. Only on an existing item —
                  a credential cannot exist before the item does. */}
              {item?.id && <VaultPasskeyList itemId={item.id} />}
            </>
          )}

          {type === 'sftp' && (
            <>
              <div>
                <FieldLabel>{t('vault.fieldProtocol')}</FieldLabel>
                <CompactSelectPicker
                  className="vault-modal-picker"
                  value={values.protocol}
                  onChange={changeProtocol}
                  options={[
                    { value: 'sftp', label: 'SFTP' },
                    { value: 'ftp', label: 'FTP' },
                    { value: 'ftps', label: 'FTPS' },
                  ]}
                  aria-label={t('vault.fieldProtocol')}
                />
              </div>
              <div className="ext-field-row">
                <div style={{ flex: 2, minWidth: 0 }}>
                  <FieldLabel required>{t('vault.fieldHost')}</FieldLabel>
                  <input
                    type="text"
                    className="modal-input"
                    style={{ width: '100%' }}
                    placeholder="host.example.com"
                    value={values.host}
                    onChange={(e) => setValue('host', e.target.value)}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <FieldLabel>{t('vault.fieldPort')}</FieldLabel>
                  <input
                    type="number"
                    className="modal-input"
                    style={{ width: '100%' }}
                    value={values.port}
                    onChange={(e) => setValue('port', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <FieldLabel required>{t('vault.fieldUsername')}</FieldLabel>
                <input
                  type="text"
                  className="modal-input"
                  style={{ width: '100%' }}
                  value={values.username}
                  onChange={(e) => setValue('username', e.target.value)}
                />
              </div>
              <div>
                <FieldLabel>{t('vault.fieldAuthMethod')}</FieldLabel>
                <CompactSelectPicker
                  className="vault-modal-picker"
                  value={values.authMethod}
                  onChange={(v) => setValue('authMethod', v)}
                  options={[
                    { value: 'password', label: t('vault.authMethodPassword') },
                    ...(values.protocol === 'sftp' ? [{ value: 'key', label: t('vault.authMethodKey') }] : []),
                  ]}
                  aria-label={t('vault.fieldAuthMethod')}
                />
              </div>
              {values.authMethod === 'key' ? (
                <>
                  <div>
                    <FieldLabel>{t('vault.fieldPrivateKey')}</FieldLabel>
                    <div className="settings-segmented" style={{ marginBottom: 8 }}>
                      <button
                        type="button"
                        onClick={() => setKeySource('text')}
                        className={`settings-segmented-btn${
                          keySource === 'text' ? ' settings-segmented-btn--active' : ''
                        }`}
                      >
                        {t('vault.keySourceText', 'Paste text')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setKeySource('file')}
                        className={`settings-segmented-btn${
                          keySource === 'file' ? ' settings-segmented-btn--active' : ''
                        }`}
                      >
                        {t('vault.keySourceFile', 'From file')}
                      </button>
                    </div>
                    {keySource === 'text' ? (
                      <textarea
                        className="modal-form-textarea"
                        rows={5}
                        placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                        value={values.privateKey}
                        onChange={(e) => setValue('privateKey', e.target.value)}
                        spellCheck={false}
                      />
                    ) : (
                      <div className="vault-pass-row">
                        <button
                          type="button"
                          className="vault-pass-secondary-btn"
                          style={{ marginTop: 0 }}
                          onClick={pickKeyFile}
                        >
                          <FolderOpen size={13} />
                          {t('vault.keyFileChoose', 'Choose file…')}
                        </button>
                        <span className="settings-row-desc" style={{ minWidth: 0 }}>
                          {keyFileName
                            ? t('vault.keyFileLoaded', 'Loaded {{name}}', { name: keyFileName })
                            : values.privateKey
                              ? t(
                                  'vault.keyFileAlready',
                                  'A key is already saved — choosing a file replaces it.'
                                )
                              : t('vault.keyFileNone', 'No file chosen.')}
                        </span>
                        <input
                          ref={keyFileInputRef}
                          type="file"
                          style={{ display: 'none' }}
                          onChange={onKeyFileInputChange}
                        />
                      </div>
                    )}
                    {keyFileError && <div className="vault-modal-error">{keyFileError}</div>}
                  </div>
                  <div>
                    <FieldLabel>{t('vault.fieldPassphrase')}</FieldLabel>
                    <PasswordInput
                      value={values.passphrase}
                      onChange={(v) => setValue('passphrase', v)}
                    />
                  </div>
                </>
              ) : (
                <div>
                  <FieldLabel>{t('vault.fieldPassword')}</FieldLabel>
                  <PasswordInput
                    value={values.password}
                    onChange={(v) => setValue('password', v)}
                    withGenerator
                    generateLabel={t('vault.generatePassword')}
                  />
                </div>
              )}
              <div>
                <FieldLabel>{t('vault.fieldInitialPath')}</FieldLabel>
                <input
                  type="text"
                  className="modal-input"
                  style={{ width: '100%' }}
                  placeholder="/var/www"
                  value={values.initialPath}
                  onChange={(e) => setValue('initialPath', e.target.value)}
                />
              </div>
            </>
          )}

          {type === 'dbConnection' && (
            <>
              <div>
                <FieldLabel>{t('vault.fieldEngine')}</FieldLabel>
                <CompactSelectPicker
                  className="vault-modal-picker"
                  value={values.engine}
                  onChange={changeEngine}
                  options={[
                    { value: 'mysql', label: 'MySQL' },
                    { value: 'postgres', label: 'PostgreSQL' },
                    { value: 'sqlite', label: 'SQLite' },
                  ]}
                  aria-label={t('vault.fieldEngine')}
                />
              </div>
              {values.engine === 'sqlite' ? (
                <div>
                  <FieldLabel required>{t('vault.fieldDbPath')}</FieldLabel>
                  <input
                    type="text"
                    className="modal-input"
                    style={{ width: '100%' }}
                    placeholder="/path/to/database.sqlite"
                    value={values.path}
                    onChange={(e) => setValue('path', e.target.value)}
                  />
                </div>
              ) : (
                <>
              <div className="ext-field-row">
                <div style={{ flex: 2, minWidth: 0 }}>
                  <FieldLabel required>{t('vault.fieldHost')}</FieldLabel>
                  <input
                    type="text"
                    className="modal-input"
                    style={{ width: '100%' }}
                    placeholder="db.example.com"
                    value={values.host}
                    onChange={(e) => setValue('host', e.target.value)}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <FieldLabel>{t('vault.fieldPort')}</FieldLabel>
                  <input
                    type="number"
                    className="modal-input"
                    style={{ width: '100%' }}
                    value={values.port}
                    onChange={(e) => setValue('port', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <FieldLabel required>{t('vault.fieldDatabase')}</FieldLabel>
                <input
                  type="text"
                  className="modal-input"
                  style={{ width: '100%' }}
                  value={values.database}
                  onChange={(e) => setValue('database', e.target.value)}
                />
              </div>
              <div>
                <FieldLabel required>{t('vault.fieldUsername')}</FieldLabel>
                <input
                  type="text"
                  className="modal-input"
                  style={{ width: '100%' }}
                  value={values.username}
                  onChange={(e) => setValue('username', e.target.value)}
                />
              </div>
              <div>
                <FieldLabel>{t('vault.fieldPassword')}</FieldLabel>
                <PasswordInput
                  value={values.password}
                  onChange={(v) => setValue('password', v)}
                />
              </div>
              <div>
                <label className="vault-modal-checkbox">
                  <input
                    type="checkbox"
                    checked={values.ssl}
                    onChange={(e) => setValue('ssl', e.target.checked)}
                  />
                  <span>{t('vault.fieldDbSsl')}</span>
                </label>
              </div>
                </>
              )}
            </>
          )}

          {type === 'authHeader' && (
            <>
              <div>
                <FieldLabel required>{t('vault.fieldHeaderName')}</FieldLabel>
                <input
                  type="text"
                  className="modal-input"
                  style={{ width: '100%' }}
                  placeholder="Authorization"
                  value={values.headerName}
                  onChange={(e) => setValue('headerName', e.target.value)}
                />
              </div>
              <div>
                <FieldLabel>{t('vault.fieldHeaderValue')}</FieldLabel>
                <PasswordInput
                  value={values.headerValue}
                  onChange={(v) => setValue('headerValue', v)}
                />
              </div>
            </>
          )}

          {type === 'note' && (
            <div>
              <FieldLabel>{t('vault.fieldBody')}</FieldLabel>
              <textarea
                className="modal-form-textarea"
                rows={8}
                value={values.body}
                onChange={(e) => setValue('body', e.target.value)}
              />
            </div>
          )}

          {type === 'custom' && (
            <div>
              <FieldLabel>{t('vault.customFields')}</FieldLabel>
              <div className="space-y-2">
                {values.fields.map((f, idx) => (
                  <div key={idx} className="ext-field-row">
                    <input
                      type="text"
                      className="modal-input"
                      style={{ flex: 1.2, minWidth: 0 }}
                      placeholder={t('vault.customKey')}
                      value={f.k}
                      onChange={(e) => updateCustomField(idx, { k: e.target.value })}
                    />
                    <input
                      type={f.secret ? 'password' : 'text'}
                      className="modal-input"
                      style={{ flex: 1.6, minWidth: 0 }}
                      placeholder={t('vault.customValue')}
                      value={f.v}
                      onChange={(e) => updateCustomField(idx, { v: e.target.value })}
                    />
                    <label className="ext-checkbox-label" style={{ whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        className="checkbox-brand"
                        checked={!!f.secret}
                        onChange={(e) => updateCustomField(idx, { secret: e.target.checked })}
                      />
                      {t('vault.customSecret')}
                    </label>
                    <button
                      type="button"
                      className="ext-remove-col-btn"
                      onClick={() => removeCustomField(idx)}
                      disabled={values.fields.length <= 1}
                      title={t('common.delete')}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="ext-add-col-btn" onClick={addCustomField}>
                <Plus size={12} /> {t('vault.addField')}
              </button>
            </div>
          )}

          {type !== 'note' && (
            <div>
              <FieldLabel>{t('vault.fieldNotes')}</FieldLabel>
              <textarea
                className="modal-form-textarea"
                rows={3}
                value={values.notes}
                onChange={(e) => setValue('notes', e.target.value)}
              />
            </div>
          )}

          {error && <div className="vault-modal-error">{error}</div>}
        </div>

        <div className="modal-footer-row">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={!canSave || saving}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {t('common.save')}
          </button>
        </div>
      </form>
      <VaultCapModal
        isOpen={!!capDraft}
        cap={capDraft?.cap}
        onKeepOnDevice={async () => {
          const saved = await useVaultStore.getState().putItem({ ...capDraft.item, sync: 'off' });
          // Remembered as kept for the cap, so Settings can upload it later.
          if (saved?.id) await keepItemsOnDevice([saved.id]);
          onClose();
        }}
        onClose={() => setCapDraft(null)}
      />
    </Modal>
  );
}
